/**
 * Hook to detect operating system and browser platform
 *
 * Useful for platform-specific styling and behaviors:
 * - iOS: Blur effects, bounce scroll, safe area insets
 * - Android: Ripple effects, elevation shadows
 * - Safari: Specific CSS workarounds
 *
 * @returns Object with platform detection flags
 */
export const usePlatform = () => {
  // Ensure we're in browser environment
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      isIOS: false,
      isAndroid: false,
      isSafari: false,
      isMacOS: false,
      isWindows: false,
      platform: 'unknown' as const,
    };
  }

  const userAgent = navigator.userAgent;
  const platform = navigator.platform;

  // iOS detection (iPhone, iPad, iPod)
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;

  // Android detection
  const isAndroid = /Android/.test(userAgent);

  // Safari detection (excluding Chrome on iOS which also reports as Safari)
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);

  // Desktop OS detection
  const isMacOS = /Mac/.test(platform);
  const isWindows = /Win/.test(platform);

  // Determine primary platform
  const getPlatform = (): 'ios' | 'android' | 'macos' | 'windows' | 'web' => {
    if (isIOS) return 'ios';
    if (isAndroid) return 'android';
    if (isMacOS) return 'macos';
    if (isWindows) return 'windows';
    return 'web';
  };

  return {
    isIOS,
    isAndroid,
    isSafari,
    isMacOS,
    isWindows,
    platform: getPlatform(),
  };
};

/**
 * Check if app is running as installed PWA
 */
export const useIsPWA = () => {
  if (typeof window === 'undefined') return false;

  // iOS standalone mode
  const isIOSPWA = (window.navigator as any).standalone === true;

  // Android/Desktop PWA
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

  return isIOSPWA || isStandalone;
};
